import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Phone, Mail, Building2, User, MessageSquare } from 'lucide-react'
import { useContact } from '@/hooks/useContacts'

export default function DirekturiKontakDetail() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { contact, loading } = useContact(id)

  if (loading) {
    return (
      <div className="p-6 max-w-lg">
        <div className="h-6 w-24 bg-gray-100 dark:bg-gray-800 rounded animate-pulse mb-6" />
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 space-y-4">
          {[...Array(5)].map((_, i) => <div key={i} className="h-8 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />)}
        </div>
      </div>
    )
  }

  if (!contact) {
    return (
      <div className="p-6 text-center py-20">
        <User size={40} className="text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">Kontak tidak ditemukan.</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-sm text-cerah-teal hover:underline">Kembali</button>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-lg">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:hover:text-white mb-6 transition-colors">
        <ArrowLeft size={16} /> Kembali
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="bg-cerah-teal p-6 flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-bold shrink-0 overflow-hidden">
            {contact.photo_url
              ? <img src={contact.photo_url} alt={contact.name} className="w-full h-full object-cover" />
              : contact.name.slice(0, 2).toUpperCase()
            }
          </div>
          <div>
            <h1 className="text-white font-bold text-lg">{contact.name}</h1>
            {contact.position && <p className="text-white/80 text-sm">{contact.position}</p>}
            {contact.organization && <p className="text-white/60 text-sm">{contact.organization}</p>}
          </div>
        </div>

        {/* Details */}
        <div className="p-6 flex flex-col gap-4">
          {contact.phone && (
            <Row icon={<Phone size={16} className="text-cerah-teal" />} label="No. Telepon">
              <a href={`tel:${contact.phone}`} className="text-sm text-cerah-teal hover:underline">{contact.phone}</a>
            </Row>
          )}
          {contact.email && (
            <Row icon={<Mail size={16} className="text-cerah-teal" />} label="Email">
              <a href={`mailto:${contact.email}`} className="text-sm text-cerah-teal hover:underline">{contact.email}</a>
            </Row>
          )}
          {contact.organization && (
            <Row icon={<Building2 size={16} className="text-gray-400" />} label="Organisasi">
              <span className="text-sm text-gray-700 dark:text-gray-300">{contact.organization}</span>
            </Row>
          )}
          {contact.notes && (
            <Row icon={<MessageSquare size={16} className="text-gray-400" />} label="Catatan">
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{contact.notes}</p>
            </Row>
          )}
        </div>
      </div>
    </div>
  )
}

function Row({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-center shrink-0">{icon}</div>
      <div>
        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        {children}
      </div>
    </div>
  )
}
